import { useEffect, useMemo, useRef, useState } from "react";
import {
  deleteRequestedDocumentUpload,
  getEmployeeDocuments,
  uploadRequestedDocument,
} from "../api/instateamDocumentsApi";
import { AppIcon } from "../components/InstateamEmployeeComponents";
import { API_BASE_URL, toAbsoluteUrl } from "../utils/employeeHelpers";

const TABS = [
  ["all", "All Documents"],
  ["verified", "Verified"],
  ["pending", "Pending"],
  ["rejected", "Rejected"],
  ["requested", "Requested"],
];

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function statusLabel(status) {
  return String(status || "not_uploaded")
    .split("_")
    .map((part) => part[0]?.toUpperCase() + part.slice(1))
    .join(" ");
}

function statusClass(status) {
  return {
    verified: "published",
    pending: "pinned",
    requested: "scheduled",
    rejected: "danger",
    not_uploaded: "muted",
  }[status] || "muted";
}

function getDownloadFileName(file, fallbackName) {
  const originalName = String(file?.originalName || "").trim();
  if (originalName) return originalName;
  return `${String(fallbackName || "document").replace(/[\\/:*?"<>|]+/g, "-")}.pdf`;
}

export default function EmployeeDocumentsPage({ token, searchText = "" }) {
  const [documents, setDocuments] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingId, setUploadingId] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [message, setMessage] = useState(null);
  const uploadInputRef = useRef(null);
  const uploadTargetRef = useRef(null);

  async function fetchDocuments({ quiet = false } = {}) {
    if (!token) return;

    try {
      if (!quiet) setIsLoading(true);
      const payload = await getEmployeeDocuments(token);
      setDocuments(payload.documents || []);
      setMetrics(payload.metrics || null);
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      if (!quiet) setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!token) return undefined;

    const refreshDocuments = () => {
      if (!document.hidden) fetchDocuments({ quiet: true });
    };
    const intervalId = window.setInterval(refreshDocuments, 5000);
    window.addEventListener("focus", refreshDocuments);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refreshDocuments);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const requestedDocuments = documents.filter((document) => document.status === "requested");
  const rejectedDocuments = documents.filter((document) => document.status === "rejected");
  const uploadedDocuments = documents.filter((document) => document.file?.url && document.status !== "rejected");

  const visibleDocuments = documents.filter((document) => {
    if (activeTab === "all") return true;
    return document.status === activeTab;
  }).filter((document) => {
    const query = String(searchText || "").trim().toLowerCase();
    if (!query) return true;
    return [
      document.documentLabel,
      document.documentKey,
      document.status,
      document.source,
      document.file?.originalName,
      document.file?.fileName,
    ].join(" ").toLowerCase().includes(query);
  });

  const stats = useMemo(() => [
    { label: "Total Documents", value: metrics?.total || 0, note: "Tracked files", icon: "fileText", tone: "yellow" },
    { label: "Verified", value: metrics?.verified || 0, note: "Approved", icon: "checkCircle", tone: "green" },
    { label: "Pending", value: metrics?.pending || 0, note: "Waiting review", icon: "clock", tone: "yellow" },
    { label: "Rejected", value: metrics?.rejected || 0, note: "Needs re-upload", icon: "xCircle", tone: "yellow" },
  ], [metrics]);

  async function handleUpload(document, file) {
    if (!file) return;

    try {
      setUploadingId(document._id);
      setMessage(null);
      const payload = await uploadRequestedDocument(token, document._id, file);
      if (payload.request) {
        setDocuments((current) =>
          current.map((item) => (item._id === document._id ? { ...item, ...payload.request } : item))
        );
      }
      setMessage({ type: "success", text: "Document uploaded and sent for verification." });
      await fetchDocuments({ quiet: true });
      setActiveTab("pending");
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setUploadingId("");
      uploadTargetRef.current = null;
      if (uploadInputRef.current) uploadInputRef.current.value = "";
    }
  }

  async function handleDeleteUpload(document) {
    if (!window.confirm(`Delete uploaded file for ${document.documentLabel}?`)) return;

    try {
      setDeletingId(document._id);
      setMessage(null);
      await deleteRequestedDocumentUpload(token, document._id);
      setMessage({ type: "success", text: "Uploaded document removed. Please upload the correct file." });
      await fetchDocuments({ quiet: true });
      setActiveTab("requested");
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setDeletingId("");
    }
  }

  async function handleDownload(document) {
    if (!document.file?.url) return;

    try {
      setMessage(null);
      const searchParams = new URLSearchParams({
        source: document.source || "profile",
        documentKey: document.documentKey || "",
        requestId: document.source === "request" ? document._id : "",
      });
      const response = await fetch(`${API_BASE_URL}/instateam/documents/employee/download?${searchParams.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error("Unable to download document.");
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = blobUrl;
      link.download = getDownloadFileName(document.file, document.documentLabel);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    }
  }

  function UploadButton({ document, label = "Upload Document" }) {
    const isUploading = uploadingId === document._id;

    return (
      <button
        type="button"
        className="employee-upload-action"
        disabled={isUploading}
        onClick={() => {
          if (!uploadInputRef.current || isUploading) return;
          uploadTargetRef.current = document;
          uploadInputRef.current.value = "";
          uploadInputRef.current.click();
        }}
      >
        {isUploading ? "Uploading..." : label}
      </button>
    );
  }

  function DeleteUploadButton({ document }) {
    if (document.source !== "request" || !document.file?.url || document.status === "verified") {
      return null;
    }

    return (
      <button
        className="employee-delete-upload-action"
        type="button"
        disabled={deletingId === document._id}
        onClick={() => handleDeleteUpload(document)}
        aria-label={`Delete uploaded ${document.documentLabel}`}
        title={deletingId === document._id ? "Deleting..." : "Delete uploaded document"}
      >
        <AppIcon name="xCircle" />
      </button>
    );
  }

  return (
    <section className="employee-portal-page employee-documents-page">
      <input
        ref={uploadInputRef}
        className="employee-upload-input"
        type="file"
        accept="image/png,image/jpeg,image/jpg,application/pdf"
        onChange={(event) => {
          const targetDocument = uploadTargetRef.current;
          const file = event.target.files?.[0];
          if (!targetDocument || !file) return;
          handleUpload(targetDocument, file);
        }}
      />

      <div className="employee-page-heading">
        <h1>My Documents</h1>
        <div className="employee-breadcrumb">
          <span>Dashboard</span>
          <span>&gt;</span>
          <span>My Documents</span>
        </div>
      </div>

      <div className="employee-announcement-stats employee-document-stats">
        {stats.map((stat) => (
          <article key={stat.label} className="employee-stat-card employee-document-stat-card">
            <div className={`employee-large-icon ${stat.tone}`}>
              <AppIcon name={stat.icon} />
            </div>
            <div>
              <span>{stat.label}</span>
              <strong>{stat.value}</strong>
              <p>{stat.note}</p>
            </div>
          </article>
        ))}
      </div>

      <div className="employee-document-info info">
        <AppIcon name="info" />
        <div>
          <strong>Your documents</strong>
          <p>View all uploaded documents and their verification status.</p>
        </div>
      </div>

      {requestedDocuments.length ? (
        <div className="employee-document-info warning">
          <AppIcon name="alertCircle" />
          <div>
            <strong>Document Requested</strong>
            <p>You have {requestedDocuments.length} document request{requestedDocuments.length > 1 ? "s" : ""} from admin.</p>
          </div>
          <button type="button" onClick={() => setActiveTab("requested")}>View Requested Document</button>
        </div>
      ) : null}

      {rejectedDocuments.length ? (
        <div className="employee-document-info danger">
          <AppIcon name="xCircle" />
          <div>
            <strong>Document Rejected</strong>
            <p>{rejectedDocuments.length} document{rejectedDocuments.length > 1 ? "s have" : " has"} been rejected. Check the reason and re-upload.</p>
          </div>
          <button type="button" onClick={() => fetchDocuments()}>Reload</button>
        </div>
      ) : null}

      {message ? <div className={`employee-inline-alert ${message.type}`}>{message.text}</div> : null}

      <div className="employee-document-tabs">
        {TABS.map(([key, label]) => (
          <button key={key} className={activeTab === key ? "active" : ""} type="button" onClick={() => setActiveTab(key)}>
            {label}
          </button>
        ))}
      </div>

      {activeTab === "requested" ? (
        <>
          <div className="employee-request-card-list">
            {requestedDocuments.map((document) => (
              <article key={document._id} className="employee-request-card">
                <div className="employee-small-icon"><AppIcon name="fileText" /></div>
                <div>
                  <div className="employee-request-title-row">
                    <h3>{document.documentLabel}</h3>
                    <span className="employee-status-chip scheduled">Requested</span>
                  </div>
                  <p>Requested on: {formatDate(document.requestedAt)}</p>
                  <p>{document.note || "Please upload this document for verification."}</p>
                </div>
                <UploadButton document={document} />
              </article>
            ))}
          </div>

          <h2 className="employee-document-section-title">Recently Uploaded Documents</h2>
        </>
      ) : null}

      {activeTab === "rejected" ? (
        <>
          <div className="employee-request-card-list">
            {rejectedDocuments.map((document) => (
              <article key={document._id} className="employee-request-card rejected">
                <div className="employee-small-icon"><AppIcon name="fileText" /></div>
                <div>
                  <div className="employee-request-title-row">
                    <h3>{document.documentLabel}</h3>
                    <span className="employee-status-chip danger">Rejected</span>
                  </div>
                  <p>Uploaded on: {formatDate(document.uploadedAt)}</p>
                  <p><strong>Reason:</strong> {document.rejectionReason || "Please upload a clearer document."}</p>
                </div>
                <div className="employee-document-action-stack">
                  <UploadButton document={document} label="Re-upload Document" />
                  <DeleteUploadButton document={document} />
                </div>
              </article>
            ))}
          </div>

          <h2 className="employee-document-section-title">Other Documents</h2>
        </>
      ) : null}

      <article className="employee-announcements-card employee-documents-card">
        <div className="employee-table-title-row">
          <h2>{activeTab === "requested" ? "Recently Uploaded Documents" : "Documents"}</h2>
          <span>{isLoading ? "Loading..." : "Live document status"}</span>
        </div>

        <div className="employee-documents-table">
          <div className="employee-documents-header">
            <span>Document Type</span>
            <span>File Name</span>
            <span>Uploaded On</span>
            <span>Status</span>
            <span>Action</span>
            <span>Download</span>
          </div>

          {(activeTab === "requested" || activeTab === "rejected" ? uploadedDocuments : visibleDocuments).map((document) => (
            <div key={document._id} className="employee-documents-row">
              <span>{document.documentLabel}</span>
              <span>{document.file?.originalName || "-"}</span>
              <span>{formatDate(document.uploadedAt)}</span>
              <span className={`employee-status-chip ${statusClass(document.status)}`}>{statusLabel(document.status)}</span>
              <div className="employee-announcement-actions">
                {document.file?.url ? (
                  <a href={toAbsoluteUrl(document.file.url)} target="_blank" rel="noreferrer" aria-label={`View ${document.documentLabel}`}>
                    <AppIcon name="eye" />
                  </a>
                ) : null}
                {document.source === "request" && ["requested", "rejected"].includes(document.status) ? (
                  <UploadButton document={document} label={document.status === "rejected" ? "Re-upload" : "Upload"} />
                ) : null}
                {document.status === "pending" || document.status === "rejected" ? (
                  <DeleteUploadButton document={document} />
                ) : null}
              </div>
              <div className="employee-announcement-actions">
                {document.file?.url ? (
                  <button
                    type="button"
                    className="employee-document-download-link"
                    onClick={() => handleDownload(document)}
                    aria-label={`Download ${document.documentLabel}`}
                  >
                    <AppIcon name="download" />
                  </button>
                ) : null}
              </div>
            </div>
          ))}

          {!(activeTab === "requested" || activeTab === "rejected" ? uploadedDocuments : visibleDocuments).length ? (
            <div className="employee-documents-row">
              <strong>{isLoading ? "Loading documents..." : "No documents found"}</strong>
              <span>Your documents and admin requests will appear here.</span>
            </div>
          ) : null}
        </div>
      </article>
    </section>
  );
}
